<template>
  <CForm @submit="submit">
    <CModal :title="$t('approvalModal.title')" icon="thumbs-up" @close="close">
      <template #body>
        <CLegend>{{ $t('approvalModal.clientSelectTitle') }}</CLegend>
        <CContactSelector v-model="formData.approvalBy" :required="needsAuthorization" :context="context.contactContext" class="u-mb-2"
                          :emptyLabel="$t('approvalModal.clientSelectEmpty')" :label="$t('approvalModal.clientSelectDescription')"/>

        <CField v-if="formData.approvalBy" type="checkbox" v-model="formData.hasPriceApprovalPermission"
                :placeholder="$t('approvalModal.contactPriceRights')" :required="needsAuthorization"/>

        <CJobApprovalTable :jobs="orderedJobs" :context :formData/>

        <CSignatureField v-model="formData.signature" class="u-mt-4"/>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import { orderBy } from 'lodash';
  import CModal from '@/general/components/CModal.vue';
  import CLegend from '@/shared/components/form/CLegend.vue';
  import CContactSelector from '@/shared/components/form/CContactSelector.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CSignatureField from '@/shared/components/form/CSignatureField.vue';
  import CJobApprovalTable from '../components/CJobApprovalTable.vue';
  import Job, { JobApprovalData } from '../models/Job';
  import JobContext from '../models/JobContext';
  import JobService from '../services/JobService';

  const emit = defineEmits<{ (e: 'close'): void }>();

  const props = defineProps<{
    jobs: Array<Job>;
    context: JobContext;
    hasDefaultValueForApprovalPermission?: boolean;
  }>();

  const formData = reactive({
    approvalBy: props.context.contactContext.getPrimary(),
    hasPriceApprovalPermission: false,
    signature: null,
  }) as JobApprovalData;

  if (props.hasDefaultValueForApprovalPermission) {
    formData.hasPriceApprovalPermission = !props.context.dossier.isLeasing;
  }

  const orderedJobs = computed(() => orderBy(props.jobs, 'sequenceNumber'));

  const needsAuthorization = computed(() => props.jobs.some(job => job.hasChangesThatRequireAuthorisation()));

  async function submit(): Promise<void> {
    await JobService.saveApprovals(props.jobs, formData, props.context);
    close();
  }

  function close() {
    emit('close');
  }
</script>
